//deps
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

// internal deps
import { TURBO_PAYMENT_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { LOCAL_STORAGE_KEYS, LocalStorage, useEffectOnce } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// components
import OverlayTurbo from "../overlay-turbo/OverlayTurbo";

// widgets
import { TurboTooltip } from "@payment-ui/widgets/turbo-tooltip";

//constants
import { RBI_GUIDELINES_TOOLTIP_KEY, TURBO_PAYMENT_COMMS } from "@payment-ui/constants/TurboPayment";

//styles
import {
    LightningContainer,
    TooltipV2CloseIcon,
    TurboPayCommsTooltipV2,
} from "./styles/TurboPaymentWidgetCommsV2.style";

interface ITurboPaymentWidgetCommsV2Props {
    showCloseIcon?: boolean;
    isChangeModeAnimation?: boolean;
    onImpressionChangeTooltipAnimation: VoidFunction;
    onClickChangeTooltipAnimation: VoidFunction;
    eventOnImpressionTooltip?: (stripMsg?: string) => void;
    eventOnTooltipClose?: VoidFunction;
    tooltipDetails?: {
        text?: string;
        enabled?: boolean;
        key?: string;
        icon?: string;
    };
    showTurboTooltip?: boolean;
    isTurboV2?: boolean;
}

const CLOSE_ICON_COLOR = "#FFFFFF";
const CLOSE_ICON_OPACITY = "1";

function TurboPaymentWidgetCommsV2({
    isChangeModeAnimation,
    onImpressionChangeTooltipAnimation,
    onClickChangeTooltipAnimation,
    eventOnImpressionTooltip,
    eventOnTooltipClose,
    tooltipDetails,
    showTurboTooltip,
    isTurboV2 = false,
}: ITurboPaymentWidgetCommsV2Props) {
    const [showComms, setShowComms] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        if (isChangeModeAnimation) {
            setTimeout(() => {
                setMounted(true);
            }, 3.5 * 1000);
        } else {
            setMounted(true);
        }
    }, [isChangeModeAnimation]);

    useEffectOnce(() => {
        if (isChangeModeAnimation) {
            onImpressionChangeTooltipAnimation();
        }
    });

    const hideComms = useCallback(() => {
        setShowComms(false);
        if (isChangeModeAnimation) {
            onClickChangeTooltipAnimation();
        }
    }, [onClickChangeTooltipAnimation, isChangeModeAnimation]);

    const hideCommsViaOverlay = useCallback(() => {
        setShowComms(false);
    }, []);

    useEffectOnce(() => {
        if (isChangeModeAnimation) {
            const changeCommsKey = LOCAL_STORAGE_KEYS.TURBO_PAYMENT_EDUCATION_CHANGE_COMMS_SHOWN;

            if (LocalStorage.getItem(changeCommsKey) !== "true") {
                LocalStorage.setItem(changeCommsKey, "true");
                setShowComms(true);
            }
        }
    });

    const renderText = useMemo(() => {
        if (isChangeModeAnimation) {
            return TURBO_PAYMENT_COMMS.CHANGE;
        }
    }, [isChangeModeAnimation]);

    const renderCloseIconOrNext = useMemo(() => {
        if (isChangeModeAnimation) {
            return <TooltipV2CloseIcon onClick={hideComms} stroke={CLOSE_ICON_COLOR} opacity={CLOSE_ICON_OPACITY} />;
        }
    }, [hideComms, isChangeModeAnimation]);

    // Tooltip events
    const onTooltipClose = () => {
        eventOnTooltipClose?.();
    };

    const onTooltipVisible = () => {
        eventOnImpressionTooltip?.(tooltipDetails?.text);
    };

    // show generic tooltip
    if (showTurboTooltip) {
        return (
            <TurboTooltip
                onTooltipVisible={onTooltipVisible}
                onTooltipClose={onTooltipClose}
                tooltipDetails={tooltipDetails}
                bottomArrowPosition={isTurboV2 ? "left" : "right"}
                size={tooltipDetails?.key === RBI_GUIDELINES_TOOLTIP_KEY ? "large" : "default"}
            />
        );
    }

    if (!showComms || !mounted) {
        return null;
    }

    return (
        <TurboPayCommsTooltipV2 isTurboV2={isTurboV2} isChangeModeAnimation={isChangeModeAnimation}>
            {!isChangeModeAnimation && <OverlayTurbo onClick={hideCommsViaOverlay} />}
            <LightningContainer>
                <Text
                    color="text_inverse_Highest_Emphasis"
                    variant="V2.body2.neutral.medium"
                    data-testid={TURBO_PAYMENT_WIDGET_DATA_TEST_IDS.EDU_COMMS}
                    tabIndex={0}
                >
                    {renderText}
                </Text>
            </LightningContainer>
            {renderCloseIconOrNext}
        </TurboPayCommsTooltipV2>
    );
}

export default memo(TurboPaymentWidgetCommsV2);
